import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Calculator, Plus as PlusIcon } from 'lucide-react';
import { useKurs } from '@/hooks/useKurs';
import SearchableCombobox from '@/components/ui/SearchableCombobox';

const KATEGORI = ['Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'Peg', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'];
const PARFAROME_GRADE = ['Deluxe', 'Premium', 'Exclusive'];
const SATUAN = ['kg', 'liter', 'gram', 'ml'];

export default function StokBahanForm({ item, kategoriAwal, onClose, onSaved, hidePrices = false }) {
  const { kurs, kursEuro } = useKurs();

  // Fetch all existing BahanCair to build vendor list per kategori
  const { data: allBahan = [] } = useQuery({
    queryKey: ['bahan-cair-all'],
    queryFn: () => base44.entities.BahanCair.list(),
  });

  // Build unique vendor options grouped by kategori
  const vendorsByKategori = useMemo(() => {
    const map = {};
    KATEGORI.forEach(k => { map[k] = new Set(); });
    allBahan.forEach(b => {
      if (b.vendor && b.kategori && !b.dari_client) {
        const cat = b.kategori;
        if (map[cat]) map[cat].add(b.vendor.trim());
        // Also add to "all" fallback
        if (!map['_all']) map['_all'] = new Set();
        map['_all'].add(b.vendor.trim());
      }
    });
    const result = {};
    Object.entries(map).forEach(([k, s]) => {
      result[k] = Array.from(s).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
    });
    return result;
  }, [allBahan]);

  const [form, setForm] = useState({
    nama: '',
    kategori: kategoriAwal || 'Bibit',
    vendor: '',
    grade: '',
    harga_rupiah: '',
    harga_jual: '',
    harga_dollar: '',
    harga_euro: '',
    mata_uang: 'IDR',
    satuan: 'kg',
    catatan: '',
    status: 'aktif',
    dari_client: false,
    ppn_type: 'include',
    ppn_percentage: 11,
    harga_sebelum_ppn: '',
    client_owner: '',
  });

  useEffect(() => {
    if (item) {
      const initialMataUang = item.mata_uang || (item.harga_euro ? 'EUR' : (item.harga_dollar && !item.harga_rupiah ? 'USD' : 'IDR'));
      setForm({
        nama: item.nama ?? '',
        kategori: item.kategori ?? 'Bibit',
        vendor: item.vendor ?? '',
        grade: item.grade ?? '',
        harga_rupiah: item.harga_rupiah ?? '',
        harga_jual: item.harga_jual ?? '',
        harga_dollar: initialMataUang === 'USD' ? (item.harga_dollar ?? '') : '',
        harga_euro: initialMataUang === 'EUR' ? (item.harga_euro ?? '') : '',
        mata_uang: initialMataUang,
        satuan: item.satuan ?? 'kg',
        stok: item.stok ?? '',
        catatan: item.catatan ?? '',
        status: item.status ?? 'aktif',
        dari_client: item.dari_client || false,
        ppn_type: item.ppn_type || 'include',
        ppn_percentage: item.ppn_percentage ?? 11,
        harga_sebelum_ppn: item.harga_sebelum_ppn ?? item.harga_rupiah ?? '',
        client_owner: item.client_owner || '',
      });
    }
  }, [item]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // USD → Rupiah otomatis (base price only)
  useEffect(() => {
    if (form.mata_uang === 'USD' && form._lastEdited === 'valas' && form.harga_dollar && kurs) {
      setForm(p => ({ ...p, harga_sebelum_ppn: (parseFloat(p.harga_dollar) * kurs).toFixed(0) }));
    }
  }, [form.harga_dollar, form.mata_uang, kurs, form._lastEdited]);

  // EUR → Rupiah otomatis (base price only)
  useEffect(() => {
    if (form.mata_uang === 'EUR' && form._lastEdited === 'valas' && form.harga_euro && kursEuro) {
      setForm(p => ({ ...p, harga_sebelum_ppn: (parseFloat(p.harga_euro) * kursEuro).toFixed(0) }));
    }
  }, [form.harga_euro, form.mata_uang, kursEuro, form._lastEdited]);

  // Rupiah → USD/EUR otomatis
  useEffect(() => {
    if (form._lastEdited === 'rupiah' && form.harga_sebelum_ppn) {
      if (form.mata_uang === 'USD' && kurs) {
        setForm(p => ({ ...p, harga_dollar: (parseFloat(p.harga_sebelum_ppn) / kurs).toFixed(4) }));
      } else if (form.mata_uang === 'EUR' && kursEuro) {
        setForm(p => ({ ...p, harga_euro: (parseFloat(p.harga_sebelum_ppn) / kursEuro).toFixed(4) }));
      } else if (form.mata_uang === 'IDR') {
        // IDR: Hitung dollar & euro otomatis murni sebagai acuan referensi, patokan tetap IDR
        const refUsd = kurs ? (parseFloat(form.harga_sebelum_ppn) / kurs).toFixed(4) : '';
        const refEur = kursEuro ? (parseFloat(form.harga_sebelum_ppn) / kursEuro).toFixed(4) : '';
        setForm(p => ({ ...p, harga_dollar: refUsd, harga_euro: refEur }));
      }
    }
  }, [form.harga_sebelum_ppn, form.mata_uang, kurs, kursEuro, form._lastEdited]);

  // Hitung Harga Sesudah PPN otomatis
  useEffect(() => {
    if (form.harga_sebelum_ppn !== '') {
      let awal = parseFloat(form.harga_sebelum_ppn) || 0;
      if (form.ppn_type === 'exclude') {
        let pct = parseFloat(form.ppn_percentage) || 0;
        let akhir = awal * (1 + pct / 100);
        setForm(p => ({ ...p, harga_rupiah: akhir.toFixed(0) }));
      } else {
        setForm(p => ({ ...p, harga_rupiah: awal.toFixed(0) }));
      }
    }
  }, [form.harga_sebelum_ppn, form.ppn_type, form.ppn_percentage]);

  const handlePpnTypeChange = (newType) => {
    if (newType === form.ppn_type) return;
    setForm(p => ({ ...p, ppn_type: newType }));
  };

  const handlePpnPctChange = (newPct) => {
    setForm(p => ({ ...p, ppn_percentage: newPct }));
  };

  const mutation = useMutation({
    mutationFn: async (data) => {
      if (item) {
        await base44.entities.BahanCair.update(item.id, data);
        if (item.nama && data.nama && item.nama.trim() !== data.nama.trim()) {
          try {
            const oldName = item.nama;
            const newName = data.nama;
            const racikanList = await base44.entities.RacikanDatabase.list();
            const updatePromises = [];
            for (const rc of racikanList) {
              if (rc.racikan && Array.isArray(rc.racikan)) {
                let changed = false;
                rc.racikan.forEach(r => {
                  if (r.nama_bahan === oldName) {
                    r.nama_bahan = newName;
                    changed = true;
                  }
                });
                if (changed) {
                  updatePromises.push(base44.entities.RacikanDatabase.update(rc.id, { racikan: rc.racikan }));
                }
              }
            }
            if (updatePromises.length > 0) {
              await Promise.all(updatePromises);
            }
          } catch (err) { console.error('Failed to cascade update RacikanDatabase:', err); }
        }
      } else {
        await base44.entities.BahanCair.create(data);
      }
    },
    onSuccess: onSaved,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let finalRupiah = parseFloat(form.harga_rupiah);
    let finalDollar = form.mata_uang === 'USD' && form.harga_dollar ? parseFloat(form.harga_dollar) : null;
    let finalEuro = form.mata_uang === 'EUR' && form.harga_euro ? parseFloat(form.harga_euro) : null;
    let finalJual = form.harga_jual ? parseFloat(form.harga_jual) : null;

    mutation.mutate({
      nama: form.nama,
      kategori: form.kategori,
      vendor: form.vendor || undefined,
      grade: form.grade || undefined,
      harga_rupiah: finalRupiah,
      harga_jual: finalJual,
      harga_dollar: finalDollar,
      harga_euro: finalEuro,
      mata_uang: form.mata_uang,
      satuan: form.satuan,
      stok: form.stok ? parseFloat(form.stok) : undefined,
      catatan: form.catatan || undefined,
      status: form.status,
      dari_client: form.dari_client,
      ppn_type: form.ppn_type,
      ppn_percentage: form.ppn_percentage ? parseFloat(form.ppn_percentage) : 11,
      harga_sebelum_ppn: form.harga_sebelum_ppn ? parseFloat(form.harga_sebelum_ppn) : finalRupiah,
      client_owner: form.dari_client ? form.client_owner : '',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 ">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="font-display font-semibold text-lg">{item ? 'Edit' : 'Tambah'} Bahan Cair</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Kepemilikan Barang</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => set('dari_client', false)} className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${!form.dari_client ? 'bg-accent text-accent-foreground border-accent shadow-sm' : 'border-border text-muted-foreground hover:border-accent/50'}`}>Inventori Satala</button>
              <button type="button" onClick={() => { setForm(p => ({ ...p, dari_client: true, harga_rupiah: 0, harga_jual: 0, harga_dollar: '', harga_euro: '' })); }} className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.dari_client ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm' : 'border-border text-muted-foreground hover:border-emerald-500/50'}`}>Titipan Client</button>
            </div>
          </div>

          {form.dari_client && (
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 mt-2">
              <label className="text-xs font-bold text-emerald-800 mb-1 block">Nama Client / Brand Titipan *</label>
              <Input 
                value={form.client_owner || ''} 
                onChange={e => setForm(p => ({...p, client_owner: e.target.value}))} 
                placeholder="Misal: Dermatech, atau Klinik Bunga..." 
                className="border-emerald-200 focus-visible:ring-emerald-500 bg-white"
              />
              <p className="text-[10px] text-emerald-600 mt-1">Isi dengan nama brand klien secara persis agar tercatat di Data Klien mereka.</p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Kategori *</label>
            <div className="flex flex-wrap gap-2">
              {KATEGORI.map(k => (
                <button key={k} type="button" onClick={() => set('kategori', k)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.kategori === k ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground hover:border-accent/50'}`}
                >{k}</button>
              ))}
            </div>
          </div>
          {!form.dari_client && (
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Vendor / Supplier</label>
            <SearchableCombobox
              value={form.vendor}
              onValueChange={v => { set('vendor', v); if (!v.toLowerCase().includes('parfarome')) set('grade', ''); }}
              options={vendorsByKategori[form.kategori] || []}
              placeholder="Pilih atau ketik vendor baru..."
              searchPlaceholder="Cari vendor..."
              allowCustom={true}
            />
            {(vendorsByKategori[form.kategori] || []).length > 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {(vendorsByKategori[form.kategori] || []).length} vendor tersedia untuk kategori <strong>{form.kategori}</strong>
              </p>
            )}
          </div>
          )}
          {form.kategori === 'Bibit' && form.vendor?.toLowerCase().includes('parfarome') && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Grade Parfarome <span className="text-destructive">*</span></label>
              <div className="flex gap-2">
                {PARFAROME_GRADE.map(g => (
                  <button key={g} type="button" onClick={() => set('grade', g)}
                    className={`flex-1 py-2 rounded-lg border text-xs font-semibold transition-all ${form.grade === g ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground hover:border-accent/50'}`}
                  >{g}</button>
                ))}
              </div>
              {!form.grade && <p className="text-xs text-amber-600 mt-1">⚠ Wajib pilih grade untuk bibit Parfarome</p>}
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Nama Bahan *</label>
            <Input value={form.nama} onChange={e => set('nama', e.target.value)} placeholder="Contoh: 9PM, Baccarat Rouge 540" required />
          </div>

          {/* Currency selector */}
          {!form.dari_client && !hidePrices && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-700">Mata Uang Acuan Utama *</label>
                <span className="text-[10px] text-slate-400">Pilih patokan dasar harga</span>
              </div>
              <div className="grid grid-cols-3 gap-2 p-1 bg-slate-100/80 rounded-xl border border-slate-200/80">
                <button
                  type="button"
                  onClick={() => {
                    setForm(p => ({ ...p, mata_uang: 'IDR', harga_dollar: '', harga_euro: '', _lastEdited: 'rupiah' }));
                  }}
                  className={`py-2 px-3 rounded-lg text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                    form.mata_uang === 'IDR'
                      ? 'bg-emerald-700 text-white shadow-sm ring-1 ring-emerald-600'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${form.mata_uang === 'IDR' ? 'bg-emerald-300 animate-pulse' : 'bg-slate-300'}`} />
                  IDR (Rp)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForm(p => ({ ...p, mata_uang: 'USD', _lastEdited: 'rupiah' }));
                  }}
                  className={`py-2 px-3 rounded-lg text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                    form.mata_uang === 'USD'
                      ? 'bg-[#8C6208] text-white shadow-sm ring-1 ring-[#8C6208]'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${form.mata_uang === 'USD' ? 'bg-amber-300 animate-pulse' : 'bg-slate-300'}`} />
                  USD ($)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForm(p => ({ ...p, mata_uang: 'EUR', _lastEdited: 'rupiah' }));
                  }}
                  className={`py-2 px-3 rounded-lg text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                    form.mata_uang === 'EUR'
                      ? 'bg-blue-700 text-white shadow-sm ring-1 ring-blue-600'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${form.mata_uang === 'EUR' ? 'bg-blue-300 animate-pulse' : 'bg-slate-300'}`} />
                  EUR (€)
                </button>
              </div>

              {/* Helper badge explaining currency benchmark */}
              <div className="mt-2">
                {form.mata_uang === 'IDR' && (
                  <div className="p-2 rounded-lg bg-emerald-50/80 border border-emerald-200/70 text-[11px] text-emerald-800 flex items-center gap-2">
                    <span className="font-bold text-xs">🔒 Patokan Rupiah Murni:</span>
                    <span>Harga terkunci dalam Rp dan tidak akan pernah bergeser akibat kurs valas.</span>
                  </div>
                )}
                {form.mata_uang === 'USD' && (
                  <div className="p-2 rounded-lg bg-amber-50/80 border border-amber-200/70 text-[11px] text-amber-900 flex items-center gap-2">
                    <span className="font-bold text-xs">💵 Patokan Dollar ($):</span>
                    <span>Harga dihitung dalam USD dan nilai Rupiah akan otomatis menyesuaikan kurs berjalan.</span>
                  </div>
                )}
                {form.mata_uang === 'EUR' && (
                  <div className="p-2 rounded-lg bg-blue-50/80 border border-blue-200/70 text-[11px] text-blue-900 flex items-center gap-2">
                    <span className="font-bold text-xs">💶 Patokan Euro (€):</span>
                    <span>Harga dihitung dalam EUR dan nilai Rupiah akan otomatis menyesuaikan kurs berjalan.</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Harga */}
          {!form.dari_client && !hidePrices && (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Status PPN Harga Beli</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handlePpnTypeChange('include')}
                    className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                      form.ppn_type === 'include'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm'
                        : 'border-border text-muted-foreground hover:border-accent/50'
                    }`}
                  >
                    Include PPN
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePpnTypeChange('exclude')}
                    className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                      form.ppn_type === 'exclude'
                        ? 'bg-amber-50 text-amber-700 border-amber-300 shadow-sm'
                        : 'border-border text-muted-foreground hover:border-accent/50'
                    }`}
                  >
                    Exclude PPN
                  </button>
                </div>
              </div>

              {form.ppn_type === 'exclude' && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Persentase PPN (%)</label>
                  <Input 
                    type="number" step="0.1" min="0"
                    value={form.ppn_percentage} 
                    onChange={e => handlePpnPctChange(Number(e.target.value))} 
                  />
                </div>
              )}

              {/* Price inputs based on selected currency */}
              {form.mata_uang === 'IDR' ? (
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">
                      {form.ppn_type === 'include' ? 'Harga Beli Satuan (Rp) *' : 'Harga Satuan Sebelum PPN (Rp) *'}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">Rp</span>
                      <Input 
                        type="number" 
                        value={form.harga_sebelum_ppn} 
                        onChange={e => setForm(p => ({ ...p, harga_sebelum_ppn: e.target.value, _lastEdited: 'rupiah' }))} 
                        placeholder="Contoh: 1000000" 
                        required 
                        className="pl-10 h-10 font-bold text-sm bg-white border-slate-200 focus:border-[#8C6208]"
                      />
                    </div>
                  </div>
                  {/* Tampilan Acuan Otomatis Dollar */}
                  {form.harga_sebelum_ppn && kurs > 0 && (
                    <div className="p-2.5 rounded-xl bg-amber-50/60 border border-amber-200/80 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-bold tracking-wider text-amber-800 bg-amber-200/60 px-1.5 py-0.5 rounded">
                          Acuan Dollar
                        </span>
                        <span className="text-xs font-black text-amber-900">
                          ${(parseFloat(form.harga_sebelum_ppn) / kurs).toFixed(2)}
                        </span>
                        {kursEuro > 0 && (
                          <span className="text-xs font-semibold text-blue-700 ml-1">
                            (€{(parseFloat(form.harga_sebelum_ppn) / kursEuro).toFixed(2)})
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-slate-500 font-medium">
                        Kurs $1 ≈ Rp {Number(kurs).toLocaleString('id-ID')}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    {form.mata_uang === 'USD' ? (
                      <>
                        <label className="text-xs font-semibold text-slate-700 mb-1 block">Harga Beli USD ($) *</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-amber-600">$</span>
                          <Input 
                            type="number" 
                            step="0.01" 
                            value={form.harga_dollar} 
                            onChange={e => setForm(p => ({ ...p, harga_dollar: e.target.value, _lastEdited: 'valas' }))} 
                            placeholder="35.00" 
                            required
                            className="pl-7 h-10 font-bold text-sm bg-white border-amber-300 text-amber-800"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <label className="text-xs font-semibold text-slate-700 mb-1 block">Harga Beli EUR (€) *</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-blue-600">€</span>
                          <Input 
                            type="number" 
                            step="0.01" 
                            value={form.harga_euro} 
                            onChange={e => setForm(p => ({ ...p, harga_euro: e.target.value, _lastEdited: 'valas' }))} 
                            placeholder="35.00" 
                            required
                            className="pl-7 h-10 font-bold text-sm bg-white border-blue-300 text-blue-800"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">
                      {form.ppn_type === 'include' ? 'Estimasi Rupiah (Rp) *' : 'Sebelum PPN (Rp) *'}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">Rp</span>
                      <Input 
                        type="number" 
                        value={form.harga_sebelum_ppn} 
                        onChange={e => setForm(p => ({ ...p, harga_sebelum_ppn: e.target.value, _lastEdited: 'rupiah' }))} 
                        placeholder="0" 
                        required 
                        className="pl-10 h-10 font-bold text-sm bg-slate-50 border-slate-200"
                      />
                    </div>
                  </div>
                </div>
              )}

              {form.ppn_type === 'exclude' && form.harga_sebelum_ppn && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                  <div className="flex justify-between items-center text-xs mb-1">
                    <span className="text-amber-800">Harga Sebelum PPN:</span>
                    <span className="font-semibold text-amber-900">Rp {parseFloat(form.harga_sebelum_ppn).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs mb-2">
                    <span className="text-amber-800">PPN ({form.ppn_percentage}%):</span>
                    <span className="font-semibold text-amber-900">+ Rp {Math.round(parseFloat(form.harga_sebelum_ppn) * (parseFloat(form.ppn_percentage) || 0) / 100).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-t border-amber-200/50 pt-2 mt-2">
                    <span className="font-bold text-amber-900">Harga Sesudah PPN:</span>
                    <span className="font-black text-amber-700">Rp {parseFloat(form.harga_rupiah || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}
              {form.ppn_type === 'include' && form.harga_sebelum_ppn && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-emerald-800">Harga (Sudah Termasuk PPN):</span>
                    <span className="font-black text-emerald-700">Rp {parseFloat(form.harga_rupiah || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Harga Jual Rupiah (Rp)</label>
                <Input type="number" value={form.harga_jual} onChange={e => set('harga_jual', e.target.value)} placeholder="Opsional (boleh dikosongkan jika belum tahu)" />
              </div>

              {form.mata_uang === 'USD' && form.harga_dollar && form.harga_rupiah && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
                  <Calculator className="w-3.5 h-3.5" /> ${parseFloat(form.harga_dollar).toFixed(2)} × Rp {kurs.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = <strong>Rp {(parseFloat(form.harga_dollar) * kurs).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </p>
              )}
              {form.mata_uang === 'EUR' && form.harga_euro && form.harga_rupiah && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
                  <Calculator className="w-3.5 h-3.5" /> €{parseFloat(form.harga_euro).toFixed(2)} × Rp {kursEuro.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = <strong>Rp {(parseFloat(form.harga_euro) * kursEuro).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </p>
              )}
            </>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Satuan *</label>
            <div className="flex gap-2">
              {SATUAN.map(s => (
                <button key={s} type="button" onClick={() => set('satuan', s)}
                  className={`flex-1 py-2 rounded-lg border text-xs font-medium transition-all ${form.satuan === s ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground hover:border-accent/50'}`}
                >{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Stok ({form.satuan})</label>
            <Input type="number" step="0.001" value={form.stok} onChange={e => set('stok', e.target.value)} placeholder="0" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Status Item *</label>
            <div className="flex gap-2">
              {[
                { v: 'aktif', l: 'Aktif' },
                { v: 'pasif', l: 'Pasif' },
                { v: 'nonaktif', l: 'Nonaktif' },
              ].map(st => (
                <button key={st.v} type="button" onClick={() => set('status', st.v)}
                  className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.status === st.v ? 'bg-primary text-primary-foreground border-primary shadow-sm' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                >
                  {st.l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button type="submit" disabled={mutation.isPending} className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground">{mutation.isPending ? 'Menyimpan...' : 'Simpan'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}